import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { TenantApiError } from "@qr-menu/api-tenant";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";
import { clearTenantToken } from "../lib/session";
import {
  MAX_PASSWORD_BYTES,
  isBlank,
  runeLength,
  utf8ByteLength,
} from "../lib/passwordValidation";

const REQUIRED_FIELDS_MESSAGE = "Mevcut parola ve yeni parola zorunludur.";
const MISMATCH_MESSAGE = "Yeni parolalar eşleşmiyor.";
const TOO_SHORT_MESSAGE = "Yeni parola en az 8 karakter olmalıdır.";
const TOO_LONG_MESSAGE = "Yeni parola çok uzun.";
const WRONG_CURRENT_MESSAGE = "Mevcut parola hatalı.";
const GENERIC_FAILURE_MESSAGE = "Parola değiştirilemedi. Lütfen tekrar deneyin.";

const VERIFIED_MESSAGE = "E-posta adresiniz doğrulanmış.";
const UNVERIFIED_MESSAGE = "E-posta adresiniz henüz doğrulanmamış.";
const RESEND_LABEL = "Doğrulama bağlantısını yeniden gönder";
const RESEND_SENDING_LABEL = "Gönderiliyor…";
const RESEND_SUCCESS_MESSAGE =
  "Doğrulama bağlantısı e-posta adresinize gönderildi.";
const RESEND_RATE_LIMITED_MESSAGE =
  "Çok fazla istek gönderildi. Lütfen daha sonra tekrar deneyin.";
const RESEND_UNAVAILABLE_MESSAGE = "E-posta gönderimi şu anda kullanılamıyor.";
const RESEND_GENERIC_FAILURE_MESSAGE =
  "Şu anda işlem gerçekleştirilemiyor. Lütfen daha sonra tekrar deneyin.";

// EmailVerificationCard is informational only — it never gates anything on
// this page or elsewhere. It fetches state via the existing tenantApi.me()
// call (no new endpoint/store) and renders one of four shapes depending on
// email_verified_at's three-state value (see tenantUserSchema):
//   - fetch not yet resolved, or it failed: render nothing. The password
//     form above is a fully independent capability and must stay usable
//     regardless; a 401 here is already handled by the client's global
//     onUnauthorized redirect, so there is nothing further to show.
//   - undefined: the backend doesn't expose the feature yet (old
//     deployment) — render nothing, never an "unverified" state.
//   - string: verified — show the confirmation, no resend control.
//   - null: unverified — show the resend control.
function EmailVerificationCard() {
  const [emailVerifiedAt, setEmailVerifiedAt] = useState<
    string | null | undefined
  >(undefined);
  const [loaded, setLoaded] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    tenantApi
      .me()
      .then((me) => {
        if (cancelled) return;
        setEmailVerifiedAt(me.user.email_verified_at);
        setLoaded(true);
      })
      .catch(() => {
        // Deliberately swallowed: a 401 is already owned by the client's
        // global onUnauthorized redirect, and any other failure just means
        // this small card stays hidden — it must never block the
        // independent password-change form above.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleResend() {
    if (resending) return;
    setResending(true);
    setResendMessage(null);
    setResendError(null);
    try {
      await tenantApi.resendEmailVerification();
      setResendMessage(RESEND_SUCCESS_MESSAGE);
    } catch (err) {
      // A bare 401 means the token itself was already invalid: the client's
      // onUnauthorized has already cleared the session and is navigating to
      // /login, so there is nothing further to show here — same convention
      // as handleSubmit's own 401 handling below.
      if (err instanceof TenantApiError && err.status === 401) {
        return;
      }
      if (err instanceof TenantApiError && err.status === 429) {
        setResendError(RESEND_RATE_LIMITED_MESSAGE);
        return;
      }
      if (err instanceof TenantApiError && err.status === 503) {
        setResendError(RESEND_UNAVAILABLE_MESSAGE);
        return;
      }
      setResendError(RESEND_GENERIC_FAILURE_MESSAGE);
    } finally {
      setResending(false);
    }
  }

  if (!loaded || emailVerifiedAt === undefined) {
    return null;
  }

  return (
    <Card className="auth-card">
      <CardHeader>
        <CardTitle>E-posta doğrulama</CardTitle>
      </CardHeader>
      <CardContent>
        {emailVerifiedAt !== null ? (
          <div className="state ok">{VERIFIED_MESSAGE}</div>
        ) : (
          <>
            <div className="state">{UNVERIFIED_MESSAGE}</div>
            <div className="login-actions">
              <button
                type="button"
                className="primary"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? RESEND_SENDING_LABEL : RESEND_LABEL}
              </button>
            </div>
            {resendMessage && <div className="state ok">{resendMessage}</div>}
            {resendError && <div className="state error">{resendError}</div>}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function AccountRoute() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(REQUIRED_FIELDS_MESSAGE);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(MISMATCH_MESSAGE);
      return;
    }
    if (isBlank(newPassword)) {
      setError(REQUIRED_FIELDS_MESSAGE);
      return;
    }
    if (runeLength(newPassword) < 8) {
      setError(TOO_SHORT_MESSAGE);
      return;
    }
    if (utf8ByteLength(newPassword) > MAX_PASSWORD_BYTES) {
      setError(TOO_LONG_MESSAGE);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await tenantApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      // No token revocation exists yet, so the old token would otherwise stay
      // valid until its own expiry. Clearing the memory-only session and
      // sending the user back to /login forces them to authenticate with the
      // new password, same as every other credential-affecting flow here.
      clearTenantToken();
      navigate("/login", { replace: true });
    } catch (err) {
      // A bare 401 means the token itself was already invalid: the client's
      // onUnauthorized has already cleared the session and is navigating to
      // /login, so there is nothing further to show here.
      if (err instanceof TenantApiError && err.status === 401) {
        return;
      }
      // Status-only mapping, never the backend's error_message text: with the
      // client-side length check above already covering the "new password
      // too long" case, a 422 here can only mean the current password was
      // wrong.
      let message = GENERIC_FAILURE_MESSAGE;
      if (err instanceof TenantApiError && err.status === 422) {
        message = WRONG_CURRENT_MESSAGE;
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Card className="auth-card">
        <CardHeader>
          <CardTitle>Parola değiştir</CardTitle>
          <CardDescription>
            Parola değiştikten sonra oturumunuz sonlandırılır; yeni parolanızla
            tekrar giriş yapmanız gerekir.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="login-form" onSubmit={handleSubmit}>
            <Field label="Mevcut parola" htmlFor="account-current-password">
              <input
                id="account-current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                spellCheck={false}
                required
              />
            </Field>
            <Field label="Yeni parola" htmlFor="account-new-password">
              <input
                id="account-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                spellCheck={false}
                required
              />
            </Field>
            <Field label="Yeni parola (tekrar)" htmlFor="account-confirm-password">
              <input
                id="account-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                spellCheck={false}
                required
              />
            </Field>
            {error && <div className="state error">{error}</div>}
            <div className="login-actions">
              <button type="submit" className="primary" disabled={submitting}>
                {submitting ? "Kaydediliyor…" : "Parolayı değiştir"}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
      <EmailVerificationCard />
    </>
  );
}
